import type { Override } from '../types/Override';
import type { ParticularKey } from '../types/ParticularKey';

/**
 * Alter a specified property on an object
 *
 * @example
 * pipe(
 *   {name: 'John', surname: 'Doe'},
 *   mapProp('surname', x => x.toUpperCase()),
 * ) // <- {name: 'John', surname: 'DOE'}
 */
export const mapProp: {
  // object's signature is not altered (map function is of type T -> T)
  <TObject, TKey extends keyof TObject>(
    key: ParticularKey<TKey>,
    fn: (prop: TObject[TKey]) => TObject[TKey],
  ): (obj: TObject) => TObject;
  // object's signature is altered (map function is of type T -> U)
  <TObject, TKey extends keyof TObject, TMappedProp>(
    key: ParticularKey<TKey>,
    fn: (prop: TObject[TKey]) => TMappedProp,
  ): (obj: TObject) => Override<TObject, { [k in TKey]: TMappedProp }>;
} =
  <TObject, TKey extends keyof TObject, TMappedProp>(
    key: TKey,
    fn: (prop: TObject[TKey]) => TMappedProp,
  ) =>
  (obj: TObject) => ({
    ...obj,
    [key]: fn(obj[key]),
  });
